import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';
import React, { useState } from 'react';
import { AuthContext, AuthData } from '../../context/auth';
import { ConfigContext, ConfigData } from '../../context/config';
import { getDateErrors } from '../../util/util';
import DefaultDatePicker, {
	DefaultDatePickerValue,
} from '../form/DefaultDatePicker';
import LargeCenterSpinner from '../LargeCenterSpinner';
import ReportTable from './ReportTable';

const useStyles = makeStyles((theme) => ({
	root: {
		flexGrow: 1,
		marginTop: '20px',
	},
	formControl: {
		minWidth: 300,
		maxWidth: 500,
		marginTop: '16px',
	},
}));

interface Props {
	configData: ConfigData;
	authData: AuthData;
	reportResults: any;
	loadingResults: boolean;
	onUpdate: (reqObj: any, hasErrors: boolean) => void;
}

const PayableCommissionsReport = (props: Props) => {
	const classes = useStyles();
	const { onUpdate, authData, reportResults, loadingResults } = props;
	const [startTimestamp, setStartTimestamp] = useState(null);
	const [endTimestamp, setEndTimestamp] = useState(null);
	const startDateErrors = getDateErrors(
		startTimestamp,
		null,
		endTimestamp,
		true,
		authData,
	);
	const endDateErrors = getDateErrors(
		endTimestamp,
		startTimestamp,
		null,
		true,
		authData,
	);

	const updateStartDate = (date: DefaultDatePickerValue) => {
		const validDate = date && !isNaN(date.getTime());
		setStartTimestamp(date);
		const req = {
			startTimestamp: validDate ? `${(date as Date).getTime()}` : null,
			endTimestamp: endTimestamp ? `${endTimestamp.getTime()}` : null,
		};
		// get updated errors to send back to reportsView
		const hasErrors = getDateErrors(date, null, endTimestamp, true, authData);
		const currentEndErrors = getDateErrors(
			endTimestamp,
			date as Date,
			null,
			true,
			authData,
		);
		onUpdate(req, !hasErrors.isValid || !currentEndErrors.isValid);
	};

	const updateEndDate = (date: DefaultDatePickerValue) => {
		const validDate = date && !isNaN(date.getTime());
		setEndTimestamp(date);
		const req = {
			startTimestamp: startTimestamp ? `${startTimestamp.getTime()}` : null,
			endTimestamp: validDate ? `${(date as Date).getTime()}` : null,
		};
		// get updated errors to send back to reportsView
		const hasErrors = getDateErrors(date, startTimestamp, null, true, authData);
		const currentStartErrors = getDateErrors(
			startTimestamp,
			null,
			date as Date,
			true,
			authData,
		);
		onUpdate(req, !hasErrors.isValid || !currentStartErrors.isValid);
	};

	const renderReportDetails = () => {
		return (
			<Grid container className={classes.root}>
				<Grid item xs={12}>
					<Grid container justifyContent='center'>
						<DefaultDatePicker
							disabled={!!reportResults || loadingResults}
							id='from'
							error={!startDateErrors.isValid}
							errorMessage={
								!startDateErrors.isValid ? startDateErrors.message : null
							}
							label='Payable Commissions From'
							style={{ marginRight: '10px' }}
							value={startTimestamp}
							maxDate={endTimestamp}
							showCalendar
							required
							onChange={(date) => updateStartDate(date)}
						/>
						<DefaultDatePicker
							disabled={!!reportResults || loadingResults}
							id='to'
							error={!endDateErrors.isValid}
							errorMessage={
								!endDateErrors.isValid ? endDateErrors.message : null
							}
							label='To'
							style={{ marginRight: '10px' }}
							value={endTimestamp}
							minDate={startTimestamp}
							showCalendar
							required
							onChange={(date) => updateEndDate(date)}
						/>
					</Grid>
				</Grid>
			</Grid>
		);
	};

	return (
		<>
			{renderReportDetails()}
			{loadingResults && <LargeCenterSpinner />}
			{reportResults && <ReportTable results={reportResults} />}
		</>
	);
};

function withContext(Component: any) {
	return (props: Partial<Props>) => (
		<AuthContext.Consumer>
			{(authData) => (
				<ConfigContext.Consumer>
					{(configData) => (
						<Component {...props} configData={configData} authData={authData} />
					)}
				</ConfigContext.Consumer>
			)}
		</AuthContext.Consumer>
	);
}

export default withContext(PayableCommissionsReport);
